import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExperimentVariant } from '@gumtree/shared/src/model/experiment';
import type { PageType } from '@gumtree/shared/src/types/client-data';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';

import {
    buildMhcIconItems,
    buildMhcTagItems,
    buildMhcTreeItems,
    type MhcCategoryItem,
} from './mhc-category-data';
import MhcCategoryTreeDrawer from './mhc-category-tree-drawer';
import {
    AllCategoriesButton,
    HorizontalList,
    IconButton,
    IconGrid,
    IconTile,
    PageDot,
    PageIndicator,
    StickyLink,
    StickyList,
    StickySentinel,
    StickyShell,
    TagLink,
    TagShell,
    TileImage,
    Wrapper,
    NavigationContainer,
} from './mhc-mobile-category-nav.style';
import { isHomepage, useActiveStickyNavElement, useHeaderStickyState } from './utils';
import { buildMhcNavigationClickEvent } from './tracking';
import type { CategoryDataItem } from '../reducer';
import AllCategoriesIcon from '../images/assets/all-categories.svg';

interface Props {
    categoryItems: CategoryDataItem[];
    pageType: PageType;
    selectedCategorySeoName?: string;
    variant: ExperimentVariant;
}

interface CategoryNavigationProps {
    items: MhcCategoryItem[];
    onOpenTree: () => void;
    selectedCategorySeoName?: string;
}

interface StickyTextNavigationProps extends CategoryNavigationProps {
    variant: ExperimentVariant;
}

const stickyPreserveAfterTreeCloseMs = 350;

const StickyTextNavigation = ({
    items,
    onOpenTree,
    selectedCategorySeoName,
}: StickyTextNavigationProps) => (
    <StickyShell data-testid="mhc-sticky-text-nav">
        <StickyList>
            {items.map((item) => (
                <StickyLink
                    aria-current={item.seoName === selectedCategorySeoName ? 'page' : undefined}
                    href={item.path || '#'}
                    key={`mhc-sticky-${item.seoName}`}
                    onClick={() => trackGA4PreNav(buildMhcNavigationClickEvent(item))}
                    selected={item.seoName === selectedCategorySeoName}
                >
                    {item.label}
                </StickyLink>
            ))}
        </StickyList>
        <AllCategoriesButton aria-label="All Categories" onClick={onOpenTree} type="button">
            <AllCategoriesIcon />
        </AllCategoriesButton>
    </StickyShell>
);

const TagNavigation = ({ items, onOpenTree, selectedCategorySeoName }: CategoryNavigationProps) => {
    const selectedTagRef = useRef<HTMLAnchorElement>(null);
    const selectedTagSeoName = items.find(
        (item) => item.seoName === selectedCategorySeoName
    )?.seoName;

    useEffect(() => {
        selectedTagRef.current?.scrollIntoView({
            block: 'center',
            inline: 'center',
            behavior: 'smooth',
        });
    }, [selectedTagSeoName]);

    return (
        <TagShell data-testid="mhc-tag-nav">
            <HorizontalList>
                {items.map((item) => {
                    const isSelected = item.seoName === selectedCategorySeoName;

                    return (
                        <TagLink
                            aria-current={isSelected ? 'page' : undefined}
                            href={item.path || '#'}
                            key={`mhc-tag-${item.seoName}`}
                            onClick={() => trackGA4PreNav(buildMhcNavigationClickEvent(item))}
                            ref={isSelected ? selectedTagRef : undefined}
                            selected={isSelected}
                        >
                            {item.label}
                        </TagLink>
                    );
                })}
            </HorizontalList>
            <AllCategoriesButton aria-label="All Categories" onClick={onOpenTree} type="button">
                <AllCategoriesIcon />
            </AllCategoriesButton>
        </TagShell>
    );
};

const ITEMS_PER_PAGE = 5;

const IconNavigation = ({
    items,
    onOpenTree,
}: Omit<CategoryNavigationProps, 'selectedCategorySeoName'>) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activePage, setActivePage] = useState(0);
    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            const scrollLeft = el.scrollLeft;
            const scrollWidth = el.scrollWidth - el.clientWidth;
            if (scrollWidth <= 0) return;

            const scrollRatio = scrollLeft / scrollWidth;
            const page = Math.round(scrollRatio * (totalPages - 1));
            setActivePage(Math.min(page, totalPages - 1));
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [totalPages]);

    return (
        <>
            <IconGrid ref={scrollRef} data-testid="mhc-icon-grid">
                {items.map((item) =>
                    item.level === 'ACTION' ? (
                        <IconButton
                            aria-label={item.label}
                            key={`mhc-icon-${item.seoName}`}
                            onClick={onOpenTree}
                            type="button"
                        >
                            <TileImage alt="" src={item.iconSrc} />
                            {item.label}
                        </IconButton>
                    ) : (
                        <IconTile
                            href={item.path || '#'}
                            key={`mhc-icon-${item.seoName}`}
                            onClick={() => trackGA4PreNav(buildMhcNavigationClickEvent(item))}
                        >
                            <TileImage alt="" src={item.iconSrc} />
                            {item.label}
                        </IconTile>
                    )
                )}
            </IconGrid>
            {totalPages > 1 && (
                <PageIndicator>
                    {Array.from({ length: totalPages }, (_, i) => (
                        <PageDot key={i} active={i === activePage} />
                    ))}
                </PageIndicator>
            )}
        </>
    );
};

const MhcMobileCategoryNavContent = ({
    categoryItems,
    pageType,
    selectedCategorySeoName,
    variant,
}: Props) => {
    const [isTreeOpen, setIsTreeOpen] = useState(false);
    const [preserveStickyWhileTreeIsClosing, setPreserveStickyWhileTreeIsClosing] = useState(false);
    const pushedHistoryRef = useRef(false);
    const preserveStickyTimeoutRef = useRef<number>();
    const isHomePage = isHomepage(pageType);
    const activeStickyNavElement = useActiveStickyNavElement(isHomePage);
    const { isSticky, sentinelRef } = useHeaderStickyState({
        enabled: isHomePage,
        preserveSticky: preserveStickyWhileTreeIsClosing,
    });
    const tagItems = buildMhcTagItems(categoryItems);
    const iconItems = buildMhcIconItems(categoryItems);
    const treeItems = buildMhcTreeItems(categoryItems);

    const clearPreserveStickyTimeout = () => {
        if (preserveStickyTimeoutRef.current) {
            window.clearTimeout(preserveStickyTimeoutRef.current);
            preserveStickyTimeoutRef.current = undefined;
        }
    };

    const openTree = () => {
        clearPreserveStickyTimeout();
        trackGA4Event<GA4.NavigationMenuExpanded>({
            event: 'navigation_menu_expanded',
            navigationCategory: 'All Categories',
        });
        setPreserveStickyWhileTreeIsClosing(true);
        setIsTreeOpen(true);
        if (!pushedHistoryRef.current) {
            window.history?.pushState?.({ mhcCategoryTree: true }, '');
            pushedHistoryRef.current = true;
        }
    };

    const closeTree = (syncHistory = false) => {
        setIsTreeOpen(false);
        clearPreserveStickyTimeout();
        preserveStickyTimeoutRef.current = window.setTimeout(() => {
            setPreserveStickyWhileTreeIsClosing(false);
            preserveStickyTimeoutRef.current = undefined;
        }, stickyPreserveAfterTreeCloseMs);
        if (syncHistory && pushedHistoryRef.current && window.history.state?.mhcCategoryTree) {
            pushedHistoryRef.current = false;
            window.history.back();
        }
    };

    useEffect(() => {
        if (!isTreeOpen) return;

        const handlePopState = () => {
            pushedHistoryRef.current = false;
            closeTree();
        };
        window.addEventListener('popstate', handlePopState);

        return () => window.removeEventListener('popstate', handlePopState);
    }, [isTreeOpen]);

    useEffect(
        () => () => {
            if (preserveStickyTimeoutRef.current) {
                window.clearTimeout(preserveStickyTimeoutRef.current);
            }
        },
        []
    );

    const stickyTextNavigation =
        isSticky && activeStickyNavElement
            ? createPortal(
                <StickyTextNavigation
                    items={tagItems}
                    onOpenTree={openTree}
                    selectedCategorySeoName={selectedCategorySeoName}
                    variant={variant}
                />,
                activeStickyNavElement
              )
            : null;

    return (
        <Wrapper aria-label="Category navigation" isSticky={isSticky} variant={variant}>
            {stickyTextNavigation}

            <NavigationContainer variant={variant}>
                {variant === ExperimentVariant.VARIANT_B ? (
                    <>
                        <TagNavigation
                            items={tagItems}
                            onOpenTree={openTree}
                            selectedCategorySeoName={selectedCategorySeoName}
                        />
                        <StickySentinel data-testid="mhc-sticky-boundary" ref={sentinelRef} />
                    </>
                ) : (
                    <>
                        <IconNavigation items={iconItems} onOpenTree={openTree} />
                        <StickySentinel data-testid="mhc-sticky-boundary" ref={sentinelRef} />
                    </>
                )}
            </NavigationContainer>

            <MhcCategoryTreeDrawer
                fullScreen={variant === ExperimentVariant.VARIANT_C}
                isOpen={isTreeOpen}
                onClose={closeTree}
                treeItems={treeItems}
            />
        </Wrapper>
    );
};

const MhcMobileCategoryNav = (props: Props) => {
    if (props.variant === ExperimentVariant.VARIANT_C && !isHomepage(props.pageType)) {
        return null;
    }

    return <MhcMobileCategoryNavContent {...props} />;
};

export default MhcMobileCategoryNav;
